/*
  Chat — Gorgias bridge
  ---------------------
  Every chat control in the theme is presentation-only: it announces a cancelable
  `chat:open` and leaves the outcome to whoever is listening. This file is that listener.
  It cancels the event when the widget can actually answer, and leaves it alone when it
  cannot — an uncancelled event is what lets a control fall back to its own href.

  Two things about the app embed shape the code, and both bite silently. Its loader
  publishes `window.GorgiasChat` straight away as a proxy that THROWS on every property
  but `init` — including `then`, so the object must never be resolved through a promise or
  the chain rejects before the widget has loaded. And `init()` resolving swaps the global
  for the real API, so the reference is read live at click time, never captured.

  `#gorgias-chat-container` is the second gate, checked per click: the bundle creates it,
  and it is absent whenever the widget is not on the page — outside business hours, most
  often — where `open()` would do nothing.

  The decision has to be synchronous, because cancelling is what tells the emitter chat
  took the click, so nothing is queued: a click that lands before the embed has loaded
  falls back rather than opening late over a page the visitor has moved on from.
*/
(function () {
  if (window.__chat) return;
  window.__chat = true;

  var CONTAINER = '#gorgias-chat-container';
  var READY_TIMEOUT = 10000;

  /* ---------- The app ---------- */

  // Set once `init()` resolves. A flag, not the API — see the note above.
  var initialised = false;
  var warned = false;

  function whenReady() {
    return new Promise(function (resolve, reject) {
      if (window.GorgiasChat) {
        resolve();
        return;
      }
      // The embed attaches the global well after this file runs.
      var started = new Date().getTime();
      var timer = setInterval(function () {
        if (window.GorgiasChat) {
          clearInterval(timer);
          resolve();
        } else if (new Date().getTime() - started > READY_TIMEOUT) {
          clearInterval(timer);
          reject(new Error('GorgiasChat never loaded — is the Gorgias app embed on for this theme?'));
        }
      }, 100);
    })
      .then(function () {
        // The only property the pre-init proxy allows, and its promise is the one signal
        // that the widget has finished mounting.
        if (typeof window.GorgiasChat.init !== 'function') return null;
        return window.GorgiasChat.init();
      })
      .then(function () {
        initialised = true;
      })
      .catch(function (error) {
        // Said once, because a fallback on its own looks identical to a dead button and
        // this is the only place the reason exists.
        if (!warned) {
          warned = true;
          console.warn('Chat: ' + error.message + ' Controls will fall back to their link.');
        }
      });
  }

  // The live global, or null while it is still the proxy that throws on every read.
  function api() {
    if (!initialised) return null;
    var chat = window.GorgiasChat;
    try {
      return chat && typeof chat.open === 'function' ? chat : null;
    } catch (error) {
      return null;
    }
  }

  /* ---------- Opening ---------- */

  document.addEventListener('chat:open', function (e) {
    // Leaving the event alone is the fallback: the emitter keeps the click and follows
    // whatever href the control carries.
    var chat = api();
    if (!chat || !document.querySelector(CONTAINER)) return;

    // Taking the event is what makes the click ours; the emitter stops driving it here.
    e.preventDefault();
    chat.open();
  });

  /* ---------- Lifecycle ---------- */

  whenReady();
})();
